import { useNavigate } from 'react-router-dom'
import { SectionHeader } from '@/components/shared/SectionHeader'
import { MovesSection } from '@/components/dashboard/MovesSection'
import { SeriesSection } from '@/components/dashboard/SeriesSection'
import { TagsSection } from '@/components/dashboard/TagsSection'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { ScrollIndicators } from '@/components/dashboard/ScrollIndicators'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ErrorState } from '@/components/shared/ErrorState'
import { useMoves } from '@/hooks/useMoves'
import { useSeries } from '@/hooks/useSeriesList'
import { useTagsList } from '@/hooks/useTagsList'
import { useScrollInView } from '@/hooks/useScrollInView'

export function DashboardPage() {
  const navigate = useNavigate()
  const [statsContainerRef, statsActiveIndex] = useScrollInView<HTMLElement>()

  // Fetch dashboard data from API
  const {
    data: movesData,
    isLoading: movesLoading,
    isError: movesError,
    error: movesErrorObj,
    refetch: refetchMoves,
  } = useMoves({ limit: 5 })

  const {
    data: seriesData,
    isLoading: seriesLoading,
    isError: seriesError,
    error: seriesErrorObj,
    refetch: refetchSeries,
  } = useSeries({ limit: 5, includeRelations: true })

  const {
    data: tagsData,
    isLoading: tagsLoading,
    isError: tagsError,
    error: tagsErrorObj,
    refetch: refetchTags,
  } = useTagsList({ limit: 5 })

  // Extract data
  const moves = movesData?.moves || []
  const movesTotal = movesData?.total || 0
  const series = seriesData?.series || []
  const seriesTotal = seriesData?.total || 0
  const tags = tagsData?.tags || []
  const tagsTotal = tagsData?.total || 0

  // Check if all data is loading
  const isLoading = movesLoading || seriesLoading || tagsLoading

  // Check if any critical error occurred
  const hasError = movesError || seriesError || tagsError

  const handleCreateMove = () => {
    navigate('/moves/new')
  }

  const handleCreateSeries = () => {
    navigate('/series/new')
  }

  const handleCreateTag = () => {
    navigate('/tags/new')
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9F9F9]">
        <LoadingSpinner />
      </div>
    )
  }

  // Error state
  if (hasError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9F9F9]">
        <ErrorState
          error={
            movesErrorObj ||
            seriesErrorObj ||
            tagsErrorObj ||
            'Failed to load dashboard'
          }
          message="Failed to load dashboard data"
          onRetry={() => {
            refetchMoves()
            refetchSeries()
            refetchTags()
          }}
        />
      </div>
    )
  }

  return (
    <div className="overscroll-behavior-y-contain min-h-screen bg-[#F9F9F9]">
      <main className="mx-auto max-w-6xl space-y-8 py-6 sm:px-6">
        {/* Analytics Cards */}
        <div>
          <section
            ref={statsContainerRef}
            className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 sm:mx-0 sm:grid sm:snap-none sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-4"
          >
            <StatsCard
              icon={
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              }
              label="Total Moves"
              value={movesTotal}
              className="w-[90vw] flex-shrink-0 snap-center sm:w-auto"
              variant="primary"
            />
            <StatsCard
              icon={
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              }
              label="Series"
              value={seriesTotal}
              variant="accent"
              className="w-[90vw] flex-shrink-0 snap-center sm:w-auto"
            />
            <StatsCard
              icon={
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
              }
              label="Tags"
              value={tagsTotal}
              variant="success"
              className="w-[90vw] flex-shrink-0 snap-center sm:w-auto"
            />
            <StatsCard
              icon={
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              label="Recently Added"
              value={moves.length}
              variant="default"
              className="w-[90vw] flex-shrink-0 snap-center sm:w-auto"
            />
          </section>
          <ScrollIndicators total={4} activeIndex={statsActiveIndex} />
        </div>

        {/* Moves Section */}
        <section className="px-4 sm:px-0">
          <SectionHeader
            title="Moves"
            viewAllLink="/moves"
            action={{
              label: 'Create Move',
              icon: (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              ),
              onClick: handleCreateMove,
            }}
          />
          <MovesSection moves={moves} />
        </section>

        {/* Series Section */}
        <section className="px-4 sm:px-0">
          <SectionHeader
            title="Series"
            viewAllLink="/series"
            action={{
              label: 'Create Series',
              icon: (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              ),
              onClick: handleCreateSeries,
            }}
          />
          <SeriesSection series={series} />
        </section>

        {/* Tags Section */}
        <section className="px-4 sm:px-0">
          <SectionHeader
            title="Tags"
            viewAllLink="/tags"
            action={{
              label: 'Create Tag',
              icon: (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              ),
              onClick: handleCreateTag,
            }}
          />
          <TagsSection tags={tags} />
        </section>
      </main>
    </div>
  )
}
